import {
  buildFetchOptionsWithAuth,
  fetchJson,
  showApiError,
  useMutation,
  useQueryClient,
} from '@fv/client-core'
import type {
  CreateSpotQuoteRequestWithLoadsDTO,
  TargetPrice,
} from '@fv/client-types'
import type { LoadBidExpiration, Mode } from '@fv/models'

import { apiUri } from '@/constants'
import type { HydratedRecipient } from '@/types'

import {
  type QuoteSettings,
  quoteSettingsKey,
} from '../settings/useQuoteSettings'
import { loadKeys } from '../shipments/useLoad'

type ApiResponse = {
  errorMessage?: {
    errors?: string[]
    message?: string
    name?: string
    requestId?: string
    status: number
  }
  ok: boolean
  json: HydratedRecipient[]
}

async function sendSpotQuoteRequests(
  dto: CreateSpotQuoteRequestWithLoadsDTO & {
    loadIds: string[]
  },
): Promise<
  Array<{
    id: string
    spotQuoteRecipients: HydratedRecipient[]
  }>
> {
  const {
    equipmentTypes,
    emails,
    loadIds,
    selectedGroupIds,
    shipperMessage,
    targetPrices,
    bidExpirations: bidExpirationDates,
  } = dto

  const getTargetPrice = (id: string) => {
    if (id && targetPrices && targetPrices.length > 0) {
      const tp = targetPrices.find(t => t.loadId === id)
      return tp ? tp : null
    } else {
      return null
    }
  }

  const getBidExpirationDate = (id: string) => {
    if (id && bidExpirationDates && bidExpirationDates.length > 0) {
      const bid = bidExpirationDates.find(b => b.loadId === id)
      return bid && bid.bidExpiration?.utc ? bid.bidExpiration : null
    } else {
      return null
    }
  }

  const buildOptions = (
    tp: TargetPrice | null,
    bidExpiration: LoadBidExpiration | null,
  ) => {
    return buildFetchOptionsWithAuth({
      body: JSON.stringify({
        emails,
        shipperMessage,
        ...(equipmentTypes && { equipmentTypes }),
        ...(selectedGroupIds && { selectedGroupIds }),
        ...(tp && {
          targetPrice: tp.targetPrice,
          targetPriceCurrency: tp.targetPriceCurrency,
        }),
        ...(bidExpiration && { bidExpiration }),
      }),
      method: 'POST',
    })
  }

  const responses = await Promise.all(
    loadIds.map(async id => {
      const tp = getTargetPrice(id)
      const bidExpDate = getBidExpirationDate(id)
      const options = buildOptions(tp, bidExpDate)
      const endpoint = `${apiUri}/spot-quotes/${id}/spot-quote-requests`
      const response = (await fetchJson(endpoint, options)) as ApiResponse
      return { id, response }
    }),
  )

  if (responses.every(res => res.response.ok)) {
    return responses.map(res => ({
      id: res.id,
      spotQuoteRecipients: res.response.json,
    }))
  } else {
    const error = responses.find(r => !r.response.ok)?.response
    if (error) {
      throw error.json
    }
    return []
  }
}

export function useSendSpotQuoteRequests(mode: Mode = 'truckload') {
  const queryClient = useQueryClient()

  return useMutation(sendSpotQuoteRequests, {
    onSettled: (loads, error, dto) => {
      if (error) return showApiError('Unable to send out for quotes', error)

      loads?.forEach(load => {
        queryClient.setQueryData(
          ['spot-quote-recipients', load.id],
          () => load.spotQuoteRecipients,
        )

        // Invalidate the load query to refetch updated equipment data from the server
        if (dto.equipmentTypes?.length) {
          queryClient.invalidateQueries({ queryKey: loadKeys.id(load.id) })
        }
      })

      if (dto.selectedGroupIds?.length) {
        queryClient.setQueryData(
          [quoteSettingsKey, mode],
          (prev?: QuoteSettings) => ({
            accessorialTypes: prev?.accessorialTypes ?? [],
            apiEnabledCarriers: prev?.apiEnabledCarriers ?? [],
            equipmentTypes: prev?.equipmentTypes ?? [],
            packageTypes: prev?.packageTypes ?? [],
            recentSpotQuoteGroups: dto.selectedGroupIds?.map(groupId => ({
              groupId,
            })),
          }),
        )
      }
    },
  })
}
